
import React, { useState, useEffect } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, parseISO, addMinutes, startOfWeek, endOfWeek, eachDayOfInterval as eachDayOfWeek, addDays, subDays, startOfDay, endOfDay } from 'date-fns';
import { ChevronLeft, ChevronRight, Plus, Edit, Trash2, Calendar as CalendarIcon, Clock, MapPin, Grid, List, CalendarDays, Navigation, Cloud, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import EventMap from '../components/EventMap';
import LocationPicker from '../components/LocationPicker';
import { getApiUrl } from "../config/api";

export default function Calendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('day'); // 'day', 'week', 'month'
  const [events, setEvents] = useState([]);
  const [monthEvents, setMonthEvents] = useState([]);
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingEvent, setDeletingEvent] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    event_date: '',
    start_time: '',
    duration_minutes: 60,
    description: '',
    location: '',
    latitude: null,
    longitude: null,
    event_type: 'general',
    is_all_day: false
  });

  // Separate state for time input display (to avoid conversion issues)
  const [timeInputValue, setTimeInputValue] = useState('');

  // Functions for external services
  const openGoogleMaps = (event) => {
    let url;
    if (event.latitude && event.longitude) {
      url = `https://www.google.com/maps?q=${event.latitude},${event.longitude}`;
    } else if (event.location) {
      url = `https://www.google.com/maps?q=${encodeURIComponent(event.location)}`;
    } else {
      return;
    }
    window.open(url, '_blank');
  };

  const getDirections = (event) => {
    let url;
    if (event.latitude && event.longitude) {
      url = `https://www.google.com/maps/dir/?api=1&destination=${event.latitude},${event.longitude}`;
    } else if (event.location) {
      url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(event.location)}`;
    } else {
      return;
    }
    window.open(url, '_blank');
  };

  const getWeather = (event) => {
    let url;
    if (event.latitude && event.longitude) {
      url = `https://www.google.com/search?q=weather+${event.latitude},${event.longitude}`;
    } else if (event.location) {
      url = `https://www.google.com/search?q=weather+${encodeURIComponent(event.location)}`;
    } else {
      return;
    }
    window.open(url, '_blank');
  };

  // Sync time input value when form data changes (e.g., when editing an event)
  useEffect(() => {
    if (editingEvent && editingEvent.start_time) {
      setTimeInputValue(formatTimeForInput(editingEvent.start_time));
    }
  }, [editingEvent]);



  // Fetch events based on current view
  const fetchEvents = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      if (view === 'day') {
        // Fetch events for the selected day (for the day list)
        const dayStart = format(startOfDay(currentDate), 'yyyy-MM-dd');
        const dayEnd = format(endOfDay(currentDate), 'yyyy-MM-dd');

        // Also fetch events for the whole month (for Upcoming Events section)
        const monthStart = format(startOfMonth(currentDate), 'yyyy-MM-dd');
        const monthEnd = format(endOfMonth(currentDate), 'yyyy-MM-dd');

        const [dayResponse, monthResponse] = await Promise.all([
          axios.get(`${getApiUrl("/api/calendar/events")}?start_date=${dayStart}&end_date=${dayEnd}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${getApiUrl("/api/calendar/events")}?start_date=${monthStart}&end_date=${monthEnd}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        setEvents(dayResponse.data);
        setMonthEvents(monthResponse.data);
      } else {
        // Week or Month views
        let startDate, endDate;
        if (view === 'week') {
          startDate = format(startOfWeek(currentDate, { weekStartsOn: 0 }), 'yyyy-MM-dd');
          endDate = format(endOfWeek(currentDate, { weekStartsOn: 0 }), 'yyyy-MM-dd');
        } else {
          startDate = format(startOfMonth(currentDate), 'yyyy-MM-dd');
          endDate = format(endOfMonth(currentDate), 'yyyy-MM-dd');
        }

        const response = await axios.get(`${getApiUrl("/api/calendar/events")}?start_date=${startDate}&end_date=${endDate}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setEvents(response.data);
        setMonthEvents([]);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [currentDate, view]);

  // Calendar navigation based on view
  const nextPeriod = () => {
    switch (view) {
      case 'day':
        setCurrentDate(addDays(currentDate, 1));
        break;
      case 'week':
        setCurrentDate(addDays(currentDate, 7));
        break;
      case 'month':
        setCurrentDate(addMonths(currentDate, 1));
        break;
    }
  };

  const prevPeriod = () => {
    switch (view) {
      case 'day':
        setCurrentDate(subDays(currentDate, 1));
        break;
      case 'week':
        setCurrentDate(subDays(currentDate, 7));
        break;
      case 'month':
        setCurrentDate(subMonths(currentDate, 1));
        break;
    }
  };

  // Get calendar days based on view
  const getCalendarDays = () => {
    switch (view) {
      case 'day':
        return [currentDate];
      case 'week':
        const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
        const weekEnd = endOfWeek(currentDate, { weekStartsOn: 0 });
        return eachDayOfWeek({ start: weekStart, end: weekEnd });
      case 'month':
      default:
        const start = startOfMonth(currentDate);
        const end = endOfMonth(currentDate);
        
        // Get the day of week the month starts on (0 = Sunday, 1 = Monday, etc.)
        const startDayOfWeek = start.getDay();
        
        // Create array of all days to display
        const days = [];
        
        // Add padding days from previous month
        for (let i = startDayOfWeek - 1; i >= 0; i--) {
          const paddingDate = new Date(start);
          paddingDate.setDate(start.getDate() - (startDayOfWeek - i));
          days.push(paddingDate);
        }
        
        // Add all days of current month
        const monthDays = eachDayOfInterval({ start, end });
        days.push(...monthDays);
        
        // Add padding days from next month to complete the grid
        const remainingSlots = 42 - days.length; // 6 rows * 7 days = 42
        for (let i = 1; i <= remainingSlots; i++) {
          const paddingDate = new Date(end);
          paddingDate.setDate(end.getDate() + i);
          days.push(paddingDate);
        }
        
        return days;
    }
  };

  const calendarDays = getCalendarDays();

  // Get display title based on view
  const getDisplayTitle = () => {
    switch (view) {
      case 'day':
        return format(currentDate, 'EEEE, MMMM d, yyyy');
      case 'week':
        const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
        const weekEnd = endOfWeek(currentDate, { weekStartsOn: 0 });
        return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;
      case 'month':
      default:
        return format(currentDate, 'MMMM yyyy');
    }
  };

  // Get events for a specific date
  const getEventsForDate = (date) => {
    return events.filter(event => isSameDay(parseISO(event.event_date), date));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      if (editingEvent) {
        const token = localStorage.getItem('token');
        await axios.put(`${getApiUrl("/api/calendar/events")}/${editingEvent.id}`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      } else {
        const token = localStorage.getItem('token');
        await axios.post(getApiUrl('/api/calendar/events'), formData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      }
      
      setShowEventForm(false);
      setEditingEvent(null);
      resetForm();
      fetchEvents();
    } catch (error) {
      console.error('Error saving event:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle event deletion confirmation
  const confirmDeleteEvent = (event) => {
    setDeletingEvent(event);
    setShowDeleteConfirm(true);
  };

  // Handle event deletion
  const handleDeleteEvent = async (eventId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${getApiUrl("/api/calendar/events")}/${eventId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      fetchEvents();
      
      // Close the main event form modal if it's open (when deleting from edit form)
      if (showEventForm) {
        setShowEventForm(false);
        setEditingEvent(null);
        resetForm();
      }
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      event_date: '',
      start_time: '',
      duration_minutes: 60,
      description: '',
      location: '',
      latitude: null,
      longitude: null,
      event_type: 'general',
      is_all_day: false
    });
    setTimeInputValue(''); // Clear time input display
  };

  // Open form for new event
  const openNewEventForm = (date) => {
    setSelectedDate(date);
    setFormData({
      ...formData,
      event_date: format(date, 'yyyy-MM-dd'),
      start_time: '' // Ensure start_time is cleared for new events
    });
    setTimeInputValue(''); // Clear time input display
    setShowEventForm(true);
    setEditingEvent(null);
  };

  // Open form for editing event
  const openEditEventForm = (event) => {
    setEditingEvent(event);
    
    // Handle the event_date - it might be an ISO string, so extract just the date part
    let eventDate = event.event_date;
    if (eventDate && typeof eventDate === 'string') {
      // If it's an ISO string like "2024-01-15T00:00:00.000Z", extract just "2024-01-15"
      if (eventDate.includes('T')) {
        eventDate = eventDate.split('T')[0];
      }
    }
    
    // Ensure location data is properly handled for Entry events
    let location = event.location || '';
    let latitude = event.latitude || null;
    let longitude = event.longitude || null;
    
    // For Entry events, if no location is set, don't show the map
    if (event.event_type === 'rodeo' && !location) {
      location = '';
      latitude = null;
      longitude = null;
    }
    
    const newFormData = {
      name: event.name,
      event_date: eventDate || format(new Date(), 'yyyy-MM-dd'),
      start_time: event.start_time || '',
      duration_minutes: event.duration_minutes || 60,
      description: event.description || '',
      location: location,
      latitude: latitude,
      longitude: longitude,
      event_type: event.event_type || 'general',
      is_all_day: event.is_all_day || false
    };
    
    setFormData(newFormData);
    // Set the time input display value to the formatted time
    setTimeInputValue(event.start_time ? formatTimeForInput(event.start_time) : '');
    setShowEventForm(true);
  };

  // Format time for display
  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return format(date, 'h:mm a');
  };

  // Get end time
  const getEndTime = (startTime, durationMinutes) => {
    if (!startTime) return '';
    const [hours, minutes] = startTime.split(':');
    const startDate = new Date();
    startDate.setHours(parseInt(hours), parseInt(minutes));
    const endDate = addMinutes(startDate, durationMinutes);
    return format(endDate, 'h:mm a');
  };

  // Format time for input field (convert 24h to 12h AM/PM)
  const formatTimeForInput = (time) => {
    if (!time || time === '') return '';
    try {
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours);
      const minute = parseInt(minutes);
      
      // Validate the parsed values
      if (isNaN(hour) || isNaN(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
        return '';
      }
      
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${displayHour}:${minute.toString().padStart(2, '0')} ${ampm}`;
    } catch (error) {
      console.error('Error formatting time:', error);
      return '';
    }
  };

  // Parse time input from user (convert 12h AM/PM to 24h)
  const parseTimeInput = (input) => {
    if (!input) return '';
    
    // Remove extra spaces and convert to lowercase for easier parsing
    const cleanInput = input.trim().toLowerCase();
    
    // Match patterns like "2:30 pm", "14:30", "2:30pm", etc.
    const timePattern = /^(\d{1,2}):(\d{2})\s*(am|pm)?$/;
    const match = cleanInput.match(timePattern);
    
    if (!match) return '';
    
    let hour = parseInt(match[1]);
    const minute = parseInt(match[2]);
    const ampm = match[3];
    
    // Validate minute range
    if (minute < 0 || minute > 59) return '';
    
    // Convert 12-hour to 24-hour format
    if (ampm) {
      if (ampm === 'pm' && hour !== 12) {
        hour += 12;
      } else if (ampm === 'am' && hour === 12) {
        hour = 0;
      }
    } else {
      // If no AM/PM specified, assume 24-hour format
      if (hour < 0 || hour > 23) return '';
    }
    
    // Validate hour range
    if (hour < 0 || hour > 23) return '';
    
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  };

  return (
    <div className="pt-16 sm:pt-0 -m-4 sm:m-0 px-0 sm:px-6 py-4 sm:py-6 w-full overflow-x-hidden">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-4 px-4 sm:px-0">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
          <CalendarIcon className="w-6 h-6 sm:w-8 sm:h-8 text-black" />
          Calendar
        </h1>
        <div className="flex justify-center sm:justify-end">
          <button
            onClick={() => openNewEventForm(new Date())}
            className="bg-[#51380d] hover:bg-[#3d2a0a] text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <Plus size={20} />
            <span className="hidden sm:inline">Add Event</span>
            <span className="sm:hidden">Add Event</span>
          </button>
        </div>
      </div>

      {/* Calendar Header */}
      <div className="bg-white shadow-sm border border-gray-200 mb-6 mx-4 sm:mx-0 rounded-lg">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-3 sm:p-4 border-b border-gray-200">
          <div className="flex items-center justify-between sm:justify-start gap-4">
            <button
              onClick={prevPeriod}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
              {getDisplayTitle()}
            </h2>
            <button
              onClick={nextPeriod}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronRight size={20} />
            </button>
          </div>
          
          {/* View Switcher */}
          <div className="flex items-center justify-center sm:justify-end gap-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setView('day')}
              className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                view === 'day' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-1">
                <CalendarDays size={16} className="sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Day</span>
              </div>
            </button>
            <button
              onClick={() => setView('week')}
              className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                view === 'week' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-1">
                <List size={16} className="sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Week</span>
              </div>
            </button>
            <button
              onClick={() => setView('month')}
              className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                view === 'month' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-1">
                <Grid size={16} className="sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Month</span>
              </div>
            </button>
          </div>
        </div>

        {/* Calendar Grid */}
        {view === 'day' ? (
          <div className="p-3 sm:p-4 w-full">
            <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4 w-full">
              <h3 className="text-lg font-semibold mb-4">
                {format(currentDate, 'EEEE, MMMM d, yyyy')}
              </h3>
              <div className="space-y-2 sm:space-y-3">
                {getEventsForDate(currentDate).map(event => (
                  <div
                    key={event.id}
                    className="border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                                      {/* Mobile Layout */}
                  <div className="sm:hidden p-2">
                      {/* Row 1: Title and Type */}
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900 text-base sm:text-sm">{event.name}</h4>
                        {event.owner_name && (
                          <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                            {event.owner_name}
                          </span>
                        )}
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          event.event_type === 'rodeo' 
                            ? 'bg-red-100 text-red-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {event.event_type === 'rodeo' ? 'Entry' : event.event_type}
                        </span>
                      </div>
                      
                      {/* Row 2: Date and Time */}
                      <div className="flex items-center gap-3 text-sm text-gray-600 mb-2">
                        <div className="flex items-center gap-1">
                          <CalendarIcon size={14} />
                          {format(currentDate, 'MM/dd/yy')}
                        </div>
                        {event.start_time && (
                          <div className="flex items-center gap-1">
                            <Clock size={14} />
                            {formatTime(event.start_time)} - {getEndTime(event.start_time, event.duration_minutes)}
                          </div>
                        )}
                      </div>
                      
                      {/* Row 3: Location */}
                      {event.location && (
                        <div className="flex items-center gap-1 text-sm text-gray-600 mb-3">
                          <MapPin size={14} />
                          <span className="cursor-pointer hover:text-blue-600" 
                                onClick={() => openGoogleMaps(event)}>
                            {event.location}
                          </span>
                        </div>
                      )}
                      
                      {/* Row 4: Action Buttons */}
                      {event.location && (
                        <div className="flex justify-center space-x-3 mb-3">
                          <button
                            onClick={() => openGoogleMaps(event)}
                            className="flex items-center gap-1 px-2 py-1 text-xs bg-[#51380d] text-white rounded hover:bg-[#3d2a0a] transition-colors"
                            title="Open in Google Maps"
                          >
                            <MapPin size={12} />
                            Map
                          </button>
                          <button
                            onClick={() => getDirections(event)}
                            className="flex items-center gap-1 px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                            title="Get Directions"
                          >
                            <Navigation size={12} />
                            Directions
                          </button>
                          <button
                            onClick={() => getWeather(event)}
                            className="flex items-center gap-1 px-2 py-1 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700 transition-colors"
                            title="Get Weather"
                          >
                            <Cloud size={12} />
                            Weather
                          </button>
                        </div>
                      )}
                      
                      {/* Row 5: Edit and Delete Buttons */}
                      <div className="flex justify-center space-x-6 border-t border-gray-100 pt-3">
                        <button
                          onClick={() => openEditEventForm(event)}
                          className="p-2 rounded text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                          aria-label="Edit"
                          title="Edit"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => confirmDeleteEvent(event)}
                          className="p-2 rounded text-red-600 hover:text-red-800 hover:bg-red-50"
                          aria-label="Delete"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                    
                    {/* Desktop Layout */}
                    <div className="hidden sm:flex items-center justify-between p-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-gray-900">{event.name}</h4>
                          {event.owner_name && (
                            <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded ml-2">
                              {event.owner_name}
                            </span>
                          )}
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            event.event_type === 'rodeo' 
                              ? 'bg-red-100 text-red-800' 
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {event.event_type === 'rodeo' ? 'Entry' : event.event_type}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          {event.start_time && (
                            <div className="flex items-center gap-1">
                              <Clock size={14} />
                              {formatTime(event.start_time)} - {getEndTime(event.start_time, event.duration_minutes)}
                            </div>
                          )}
                          {event.location && (
                            <div className="flex items-center gap-1">
                              <MapPin size={14} />
                              <span className="cursor-pointer hover:text-blue-600" 
                                    onClick={() => openGoogleMaps(event)}>
                                {event.location}
                              </span>
                            </div>
                          )}
                        </div>
                        {event.event_type !== 'rodeo' && event.description && (
                          <p className="text-sm text-gray-600 mt-1">{event.description}</p>
                        )}
                        
                        {/* Action Buttons */}
                        {event.location && (
                          <div className="flex gap-2 mt-2">
                            <button
                              onClick={() => openGoogleMaps(event)}
                              className="flex items-center gap-1 px-2 py-1 text-xs bg-[#51380d] text-white rounded hover:bg-[#3d2a0a] transition-colors"
                              title="Open in Google Maps"
                            >
                              <MapPin size={12} />
                              Map
                            </button>
                            <button
                              onClick={() => getDirections(event)}
                              className="flex items-center gap-1 px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                              title="Get Directions"
                            >
                              <Navigation size={12} />
                              Directions
                            </button>
                            <button
                              onClick={() => getWeather(event)}
                              className="flex items-center gap-1 px-2 py-1 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700 transition-colors"
                              title="Get Weather"
                            >
                              <Cloud size={12} />
                              Weather
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => openEditEventForm(event)}
                          className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => confirmDeleteEvent(event)}
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {getEventsForDate(currentDate).length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <CalendarIcon size={48} className="mx-auto mb-4 text-gray-300" />
                    <p>No events scheduled for today</p>
                    <button
                      onClick={() => openNewEventForm(currentDate)}
                      className="mt-2 text-blue-600 hover:text-blue-700"
                    >
                      Add an event
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className={`grid gap-px bg-gray-200 overflow-x-auto w-full ${
            view === 'week' ? 'grid-cols-7' : 'grid-cols-7'
          }`}>
            {/* Day headers */}
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="bg-gray-50 p-2 sm:p-3 text-center text-xs sm:text-sm font-medium text-gray-500">
                <span className="hidden sm:inline">{day}</span>
                <span className="sm:hidden">{day.charAt(0)}</span>
              </div>
            ))}

            {/* Calendar days */}
            {calendarDays.map(day => {
              const dayEvents = getEventsForDate(day);
              const isCurrentMonth = view === 'month' ? isSameMonth(day, currentDate) : true;
              const isToday = isSameDay(day, new Date());
              const isPaddingDay = view === 'month' && !isCurrentMonth;
              const isCurrentPeriod = view === 'week' ? 
                isSameDay(day, currentDate) || 
                (day >= startOfWeek(currentDate, { weekStartsOn: 0 }) && 
                 day <= endOfWeek(currentDate, { weekStartsOn: 0 })) : true;

              return (
                <div
                  key={day.toString()}
                  className={`min-h-[80px] sm:min-h-[120px] bg-white p-1 sm:p-2 transition-colors w-full ${
                    isPaddingDay ? 'text-gray-300 bg-gray-50' : 'cursor-pointer hover:bg-gray-50'
                  } ${isToday ? 'bg-blue-50 border-2 border-blue-200' : ''} ${
                    view === 'week' && !isCurrentPeriod ? 'bg-gray-50 text-gray-400' : ''
                  }`}
                  onClick={isPaddingDay ? undefined : () => openNewEventForm(day)}
                >
                  <div className={`text-xs sm:text-sm font-medium mb-1 ${
                    isPaddingDay || (view === 'week' && !isCurrentPeriod) ? 'text-gray-300' : ''
                  }`}>
                    {format(day, 'd')}
                  </div>
                  {!isPaddingDay && (
                    <div className="space-y-0.5 sm:space-y-1">
                      {dayEvents.slice(0, view === 'week' ? 2 : 1).map(event => (
                        <div
                          key={event.id}
                          className="text-xs p-0.5 sm:p-1 rounded bg-blue-100 text-blue-800 truncate cursor-pointer w-full"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditEventForm(event);
                          }}
                        >
                          <span className="hidden sm:inline">{event.name}</span>
                          <span className="sm:hidden">{event.name.length > 6 ? event.name.substring(0, 6) + '...' : event.name}</span>
                        </div>
                      ))}
                      {dayEvents.length > (view === 'week' ? 2 : 1) && (
                        <div className="text-xs text-gray-500">
                          +{dayEvents.length - (view === 'week' ? 2 : 1)} more
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Event Form Modal */}
      {showEventForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-[70] overflow-y-auto p-4 pt-20 sm:pt-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-md max-h-[90vh] overflow-y-auto mt-4 sm:mt-0">
            <h3 className="text-lg font-semibold mb-4 sticky top-0 bg-white pb-2 z-10">
              {editingEvent ? 'Edit Event' : 'Add New Event'}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Event Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base sm:text-sm sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date *
                </label>
                <input
                  type="date"
                  required
                  value={formData.event_date}
                  onChange={(e) => setFormData({...formData, event_date: e.target.value})}
                  className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base sm:text-sm sm:text-sm"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <label className={`block text-sm font-medium mb-1 ${formData.is_all_day ? 'text-gray-400' : 'text-gray-700'}`}>
                    Start Time (e.g., 2:30 PM) *
                  </label>
                  <input
                    type="text"
                    placeholder="2:30 PM"
                    value={timeInputValue}
                    required={!formData.is_all_day}
                    onChange={(e) => {
                      // Store the raw input in the display state
                      setTimeInputValue(e.target.value);
                    }}
                    onBlur={(e) => {
                      // Parse and validate on blur
                      if (e.target.value) {
                        const timeValue = parseTimeInput(e.target.value);
                        if (timeValue) {
                          setFormData({...formData, start_time: timeValue});
                        } else {
                          // If parsing fails, clear both the input and the form data
                          setTimeInputValue('');
                          setFormData({...formData, start_time: ''});
                        }
                      } else {
                        // If empty, clear the form data
                        setFormData({...formData, start_time: ''});
                      }
                    }}
                    disabled={formData.is_all_day}
                    className={`w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base sm:text-sm ${
                      formData.is_all_day ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''
                    }`}
                  />
                </div>
                <div className="flex-1">
                  <label className={`block text-sm font-medium mb-1 ${formData.is_all_day ? 'text-gray-400' : 'text-gray-700'}`}>
                    Duration (minutes)
                  </label>
                  <input
                    type="number"
                    min="15"
                    step="15"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData({...formData, duration_minutes: parseInt(e.target.value)})}
                    disabled={formData.is_all_day}
                    className={`w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base sm:text-sm ${
                      formData.is_all_day ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''
                    }`}
                  />
                </div>
              </div>

              <LocationPicker
                location={formData.location}
                latitude={formData.latitude}
                longitude={formData.longitude}
                onLocationChange={(location) => setFormData({...formData, location})}
                onCoordinatesChange={(latitude, longitude) => setFormData({...formData, latitude, longitude})}
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows="3"
                  className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base sm:text-sm resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Event Type
                </label>
                <select
                  value={formData.event_type}
                  onChange={(e) => setFormData({...formData, event_type: e.target.value})}
                  className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base sm:text-sm"
                >
                  <option value="general">General</option>
                  <option value="rodeo">Entry</option>
                </select>
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_all_day}
                    onChange={(e) => {
                      const isAllDay = e.target.checked;
                      if (isAllDay) {
                        // Clear time fields when checking "all day"
                        setFormData({...formData, is_all_day: isAllDay, start_time: '', duration_minutes: 60});
                        setTimeInputValue(''); // Clear time input display
                      } else {
                        // Restore default duration when unchecking "all day"
                        setFormData({...formData, is_all_day: isAllDay, duration_minutes: 60});
                      }
                    }}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">All day event</span>
                </label>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 sm:py-2 px-4 rounded-lg transition-colors disabled:opacity-50 text-base sm:text-sm"
                >
                  {loading ? 'Saving...' : (editingEvent ? 'Update Event' : 'Create Event')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEventForm(false);
                    setEditingEvent(null);
                    resetForm();
                  }}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-3 sm:py-2 px-4 rounded-lg transition-colors text-base sm:text-sm"
                >
                  Cancel
                </button>
              </div>

              {editingEvent && (
                <button
                  type="button"
                  onClick={() => confirmDeleteEvent(editingEvent)}
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-3 sm:py-2 px-4 rounded-lg transition-colors text-base sm:text-sm mt-3"
                >
                  Delete Event
                </button>
              )}
            </form>

            {/* Show map for events with location - only show for new events, not when editing */}
            {formData.location && 
             formData.location.trim() && 
             !formData.is_all_day && 
             formData.location.length > 2 &&
             formData.event_type !== 'rodeo' && (
              <div className="mt-6">
                <EventMap
                  key={`event-map-${editingEvent ? 'edit' : 'new'}-${formData.location}`}
                  location={formData.location}
                  latitude={formData.latitude}
                  longitude={formData.longitude}
                  eventName={formData.name}
                  disabled={!!editingEvent} // Disable map functionality when editing
                />
              </div>
            )}

            {/* For Entry events, show a simple location display without map */}
            {formData.event_type === 'rodeo' && 
             formData.location && 
             formData.location.trim() && 
             formData.location.length > 2 && (
              <div className="mt-6">
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-300">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin size={16} className="text-gray-600" />
                    <h4 className="font-medium text-gray-900">Event Location</h4>
                  </div>
                  <p className="text-sm text-gray-700">
                    <strong>Address:</strong> {formData.location}
                  </p>
                  {formData.latitude && formData.longitude && (
                    <p className="text-xs text-gray-500 mt-1">
                      Coordinates: {parseFloat(formData.latitude).toFixed(6)}, {parseFloat(formData.longitude).toFixed(6)}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Events List */}
      <div className="bg-white shadow-sm border border-gray-200 mx-4 sm:mx-0 rounded-lg">
        <div className="p-3 sm:p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {view === 'day' ? `Events for ${format(currentDate, 'MMMM yyyy')}` : 'Upcoming Events'}
          </h3>
        </div>
        <div className="p-3 sm:p-4 w-full">
          {loading ? (
            <div className="text-center py-4">Loading events...</div>
          ) : (view === 'day' ? monthEvents.length === 0 : events.length === 0) ? (
            <div className="text-center py-8 text-gray-500">
              <CalendarIcon size={48} className="mx-auto mb-4 text-gray-300" />
              <p>No events scheduled for {view === 'day' ? format(currentDate, 'MMMM yyyy') : 'this month'}</p>
              <p className="text-sm">Click on a date to add an event</p>
            </div>
          ) : (
            <div className="space-y-2 sm:space-y-3 w-full">
              {(view === 'day' ? monthEvents : events).map(event => (
                <div
                  key={event.id}
                  className="border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {/* Mobile Layout */}
                  <div className="sm:hidden p-2">
                    {/* Row 1: Title and Type */}
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900 text-base sm:text-sm">{event.name}</h4>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        event.event_type === 'rodeo' 
                          ? 'bg-red-100 text-red-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {event.event_type === 'rodeo' ? 'Entry' : event.event_type}
                      </span>
                    </div>
                    
                    {/* Row 2: Date and Time */}
                    <div className="flex items-center gap-3 text-sm text-gray-600 mb-2">
                      <div className="flex items-center gap-1">
                        <CalendarIcon size={14} />
                        {format(parseISO(event.event_date), 'MM/dd/yy')}
                      </div>
                      {event.start_time && (
                        <div className="flex items-center gap-1">
                          <Clock size={14} />
                          {formatTime(event.start_time)} - {getEndTime(event.start_time, event.duration_minutes)}
                        </div>
                      )}
                    </div>
                    
                    {/* Row 3: Location */}
                    {event.location && (
                      <div className="flex items-center gap-1 text-sm text-gray-600 mb-3">
                        <MapPin size={14} />
                        <span className="cursor-pointer hover:text-blue-600" 
                              onClick={() => {
                                if (event.latitude && event.longitude) {
                                  window.open(`https://www.google.com/maps?q=${event.latitude},${event.longitude}`, '_blank');
                                } else if (event.location) {
                                  window.open(`https://www.google.com/maps?q=${encodeURIComponent(event.location)}`, '_blank');
                                }
                              }}>
                          {event.location}
                        </span>
                      </div>
                    )}
                    
                    {/* Row 4: Action Buttons */}
                    {event.location && (
                      <div className="flex justify-center space-x-3 mb-3">
                        <button
                          onClick={() => openGoogleMaps(event)}
                          className="flex items-center gap-1 px-2 py-1 text-xs bg-[#51380d] text-white rounded hover:bg-[#3d2a0a] transition-colors"
                          title="Open in Google Maps"
                        >
                          <MapPin size={12} />
                          Map
                        </button>
                        <button
                          onClick={() => getDirections(event)}
                          className="flex items-center gap-1 px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                          title="Get Directions"
                        >
                          <Navigation size={12} />
                          Directions
                        </button>
                        <button
                          onClick={() => getWeather(event)}
                          className="flex items-center gap-1 px-2 py-1 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700 transition-colors"
                          title="Get Weather"
                        >
                          <Cloud size={12} />
                          Weather
                        </button>
                      </div>
                    )}
                    
                    {/* Row 5: Edit and Delete Buttons */}
                    <div className="flex justify-center space-x-6 border-t border-gray-100 pt-3">
                      <button
                        onClick={() => openEditEventForm(event)}
                        className="p-2 rounded text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                        aria-label="Edit"
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => confirmDeleteEvent(event)}
                        className="p-2 rounded text-red-600 hover:text-red-800 hover:bg-red-50"
                        aria-label="Delete"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  
                  {/* Desktop Layout */}
                  <div className="hidden sm:flex items-center justify-between p-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-gray-900">{event.name}</h4>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          event.event_type === 'rodeo' 
                            ? 'bg-red-100 text-red-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {event.event_type === 'rodeo' ? 'Entry' : event.event_type}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <CalendarIcon size={14} />
                          {format(parseISO(event.event_date), 'MMM d, yyyy')}
                        </div>
                        {event.start_time && (
                          <div className="flex items-center gap-1">
                            <Clock size={14} />
                            {formatTime(event.start_time)} - {getEndTime(event.start_time, event.duration_minutes)}
                          </div>
                        )}
                        {event.location && (
                          <div className="flex items-center gap-1">
                            <MapPin size={14} />
                            <span className="cursor-pointer hover:text-blue-600" 
                                  onClick={() => {
                                    if (event.latitude && event.longitude) {
                                      window.open(`https://www.google.com/maps?q=${event.latitude},${event.longitude}`, '_blank');
                                    } else if (event.location) {
                                      window.open(`https://www.google.com/maps?q=${encodeURIComponent(event.location)}`, '_blank');
                                    }
                                  }}>
                              {event.location}
                            </span>
                          </div>
                        )}
                      </div>
                      {event.event_type !== 'rodeo' && event.description && (
                        <p className="text-sm text-gray-600 mt-1">{event.description}</p>
                      )}
                      
                      {/* Action Buttons */}
                      {event.location && (
                        <div className="flex gap-2 mt-2">
                          <button
                            onClick={() => openGoogleMaps(event)}
                            className="flex items-center gap-1 px-2 py-1 text-xs bg-[#51380d] text-white rounded hover:bg-[#3d2a0a] transition-colors"
                            title="Open in Google Maps"
                          >
                            <MapPin size={12} />
                            Map
                          </button>
                          <button
                            onClick={() => getDirections(event)}
                            className="flex items-center gap-1 px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                            title="Get Directions"
                          >
                            <Navigation size={12} />
                            Directions
                          </button>
                          <button
                            onClick={() => getWeather(event)}
                            className="flex items-center gap-1 px-2 py-1 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700 transition-colors"
                            title="Get Weather"
                          >
                            <Cloud size={12} />
                            Weather
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEditEventForm(event)}
                        className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => confirmDeleteEvent(event)}
                        className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deletingEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-[70] p-4 pt-20 sm:pt-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 mt-4 sm:mt-0">
            <div className="flex items-center mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600 mr-3" />
              <h3 className="text-lg font-semibold">Confirm Delete</h3>
            </div>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete <span className="font-semibold">"{deletingEvent.name}"</span>? This action cannot be undone.
            </p>
            <div className="flex flex-col sm:flex-row justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeletingEvent(null);
                }}
                className="w-full sm:w-auto px-4 py-3 sm:py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-base sm:text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (deletingEvent) {
                    handleDeleteEvent(deletingEvent.id);
                  }
                  setShowDeleteConfirm(false);
                  setDeletingEvent(null);
                }}
                className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-base sm:text-sm"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
